/**
 * StorefrontScopePicker — multi-select store scope control for the Pricing Rule
 * / Coupon wizard.
 *
 * Lets multi-storefront tenants (e.g. CSIDE = CSide + Sub Scrub, one ERPNext
 * company) restrict a coupon or automatic pricing rule to specific storefronts.
 *
 * CRITICAL: the value bound to each option is `storefront_key` (e.g. "0j85q3282r",
 * "subscrub") — the SAME string checkout enforcement matches against — NOT the
 * human `label`. `label` is display only. Binding label would make the scope
 * never match at checkout.
 *
 * Serializes selected keys as a comma-separated string (blank = all storefronts).
 * Renders nothing for single-store tenants (<2 storefronts), so scope stays empty
 * = all stores — matching DashboardStoreSelector behavior.
 */

import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useStoreList, getStoreColor } from "@/hooks/useStoreList";
import { ChevronDown } from "lucide-react";

type T = (k: string) => string;

interface StorefrontScopePickerProps {
  /** Comma-separated storefront_keys; "" = all storefronts. */
  value: string;
  onChange: (value: string) => void;
  t: T;
  /** Render a top divider + row padding to sit inside a card of rows. */
  withDivider?: boolean;
  /**
   * Compact, trigger-only variant for a detail-page row (label handled by the
   * parent row). Renders just the multi-select trigger, right-aligned, no
   * heading/hint block. Empty selection shows "all storefronts".
   */
  inline?: boolean;
}

export function StorefrontScopePicker({
  value,
  onChange,
  t,
  withDivider = false,
  inline = false,
}: StorefrontScopePickerProps) {
  const { data: stores = [] } = useStoreList();
  const [open, setOpen] = useState(false);

  const selectedSet = useMemo(
    () => new Set(value.split(",").map((k) => k.trim()).filter(Boolean)),
    [value],
  );

  // Single-store tenants never see this — scope stays empty = all stores.
  if (stores.length < 2) return null;

  const selectedStores = stores.filter((st) => selectedSet.has(st.storefront_key));

  const toggle = (key: string, checked: boolean) => {
    const next = new Set(selectedSet);
    if (checked) next.add(key);
    else next.delete(key);
    // Serialize in store-list order so the string stays stable regardless of
    // click order (avoids needless diffs / re-saves).
    const ordered = stores
      .filter((st) => next.has(st.storefront_key))
      .map((st) => st.storefront_key);
    onChange(ordered.join(","));
  };

  const chips = (
    <span className="flex items-center gap-1.5 min-w-0">
      {selectedStores.map((st) => (
        <span key={st.storefront_key} className="flex items-center gap-1 min-w-0">
          <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", getStoreColor(st.color_index))} />
          <span className="truncate">{st.label}</span>
        </span>
      ))}
    </span>
  );

  const picker = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "font-normal",
            inline
              ? "h-8 justify-end gap-1.5 border-0 bg-transparent px-2 text-sm text-gray-900 hover:bg-muted/40 dark:text-gray-100"
              : "h-11 w-full justify-between",
          )}
        >
          {selectedStores.length === 0 ? (
            <span className={inline ? "text-gray-500 dark:text-gray-400" : "text-muted-foreground"}>
              {inline ? t("storefront_scope_all") : t("storefront_scope_ph")}
            </span>
          ) : (
            chips
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-2", inline ? "w-56" : "w-[--radix-popover-trigger-width]")} align="end">
        {stores.map((store) => (
          <label
            key={store.storefront_key}
            className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 cursor-pointer"
          >
            <Checkbox
              checked={selectedSet.has(store.storefront_key)}
              onCheckedChange={(checked) => toggle(store.storefront_key, !!checked)}
            />
            <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", getStoreColor(store.color_index))} />
            <span className="text-sm truncate">{store.label}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );

  if (inline) return picker;

  return (
    <div className={cn(withDivider && "border-t border-border/50 mx-4 pt-4 px-4 pb-4")}>
      <label className="text-sm font-medium block">{t("storefront_scope")}</label>
      <p className="text-xs text-muted-foreground mt-1 mb-2">{t("storefront_scope_hint")}</p>
      {picker}
    </div>
  );
}
